import moment from 'moment';
import React from 'react';
import { IAgentLandingAgentData, IAgentLandingMarketStats, IAgentLandingSeriesPoint } from 'types/agent-landing-page';
import { ELeadResponseType } from 'types/lead-response';

import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import DateRangeOutlinedIcon from '@material-ui/icons/DateRangeOutlined';
import ShowChartOutlinedIcon from '@material-ui/icons/ShowChartOutlined';

import AgentLandingLeadForm, { AgentAvatar, Eyebrow, normalizeAgents } from 'components/AgentLanding/AgentLandingPrimitives';

import { stateNames } from 'utils/lib';

import {
  aggregateMonthlyLatest, InventoryChart, PricingChart, RentChart,
} from './AgentLandingCharts';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const titleCaseLocation = (value?: string) => value?.toLocaleLowerCase('en-US').replace(/(^|[\s'-])\p{L}/gu, (letter) => letter.toLocaleUpperCase('en-US'));
const fullStateName = (value?: string) => {
  if (!value) return undefined;
  return stateNames[value.trim().toUpperCase()] || titleCaseLocation(value);
};
const formatPercent = (value?: number) => (value === undefined ? 'Unavailable' : `${Math.round((Math.abs(value) <= 1 ? value * 100 : value))}%`);
const formatMetric = (value: number | undefined, formatter: (value: number) => string, suffix = '') => (value === undefined ? 'Unavailable' : `${formatter(value)}${suffix}`);
const normalizedPercent = (value?: number) => (value === undefined ? undefined : Math.min(100, Math.max(0, Math.abs(value) <= 1 ? value * 100 : value)));

const pointNearDaysAgo = (series: IAgentLandingSeriesPoint[], days: number) => {
  if (series.length < 2) return undefined;
  const sorted = [...series].sort((a, b) => a.date.localeCompare(b.date));
  const target = moment(sorted[sorted.length - 1].date).subtract(days, 'days');
  return sorted.reduce((closest, point) => (
    Math.abs(moment(point.date).diff(target, 'days')) < Math.abs(moment(closest.date).diff(target, 'days')) ? point : closest
  ));
};

const percentChange = (series: IAgentLandingSeriesPoint[]) => {
  const current = series[series.length - 1]?.value;
  const previous = pointNearDaysAgo(series, 90)?.value;
  if (current === undefined || previous === undefined || previous === 0) return undefined;
  return ((current - previous) / Math.abs(previous)) * 100;
};

const PriceTrend = ({ series }: { series: IAgentLandingSeriesPoint[] }) => {
  const change = percentChange(series);
  if (change === undefined) return <small><i>90-day trend unavailable</i></small>;
  const steady = Math.abs(change) < 1;
  return (
    <small className={`al-trend ${steady ? 'steady' : change > 0 ? 'improving' : 'worsening'}`}>
      {steady ? '→' : change > 0 ? '↑' : '↓'}
      {' '}
      {Math.abs(change).toFixed(1)}
      %
      {' '}
      <i>vs. 90 days</i>
    </small>
  );
};

export const DigitalAdHeadlineMetrics = ({ zipCodeData }: { zipCodeData: IAgentLandingMarketStats }) => {
  const { metrics, series } = zipCodeData;
  const averageDomSeries = series.averageDaysOnMarket || [];
  const currentDom = metrics.averageDaysOnMarket;
  const priorDom = pointNearDaysAgo(averageDomSeries, 90)?.value;
  const domDifference = currentDom !== undefined && priorDom !== undefined ? Math.floor(currentDom - priorDom) : undefined;

  return (
    <section className="al-metric-strip" aria-label="Market headline metrics">
      <article>
        <span>Median list price</span>
        <strong>{formatMetric(metrics.medianListPrice, currency.format)}</strong>
        <PriceTrend series={series.medianListPrice || []} />
      </article>
      <article>
        <span>Median new listing price</span>
        <strong>{formatMetric(metrics.medianNewListingPrice, currency.format)}</strong>
        <PriceTrend series={series.medianNewListingPrice || []} />
      </article>
      <article>
        <span>Price per sq ft</span>
        <strong>{formatMetric(metrics.medianPricePerSquareFoot, currency.format)}</strong>
        <PriceTrend series={series.medianPricePerSquareFoot || []} />
      </article>
      <article>
        <span>Avg. days on market</span>
        <strong>{formatMetric(currentDom, integer.format, currentDom === undefined ? '' : ' days')}</strong>
        <small className={domDifference === undefined || domDifference === 0 ? 'al-trend steady' : domDifference < 0 ? 'al-trend improving' : 'al-trend worsening'}>
          {domDifference === undefined ? '90-day trend unavailable' : `${domDifference === 0 ? '→' : domDifference < 0 ? '↓' : '↑'} ${Math.abs(domDifference)} days `}
          {domDifference !== undefined && <i className={domDifference === 0 ? undefined : 'al-trend-direction'}>{domDifference < 0 ? 'faster' : domDifference > 0 ? 'slower' : 'vs. 90 days'}</i>}
        </small>
      </article>
    </section>
  );
};

const OutcomeBar = ({ label, value, color }: { label: string; value?: number; color: 'decreased' | 'increased' | 'relisted' }) => {
  const percent = normalizedPercent(value);
  const formattedValue = formatPercent(value);

  return (
    <div className="al-outcome-item">
      <div className="al-outcome-label">
        <span>{label}</span>
        <strong>{formattedValue}</strong>
      </div>
      <div
        className="al-outcome-track"
        role="progressbar"
        aria-label={`${label}: ${formattedValue}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent === undefined ? undefined : Math.round(percent)}
      >
        <i className={`al-outcome-fill ${color}`} style={{ '--value': `${percent || 0}%` } as React.CSSProperties} />
      </div>
    </div>
  );
};

export const DigitalAdMarketCards = ({ zipCodeData }: { zipCodeData: IAgentLandingMarketStats }) => {
  const { metrics, series, narrative } = zipCodeData;
  const averageDomSeries = series.averageDaysOnMarket || [];
  const currentDom = metrics.averageDaysOnMarket;
  const priorDom = pointNearDaysAgo(averageDomSeries, 90)?.value;
  const cityAverageDom = metrics.cityAverageDaysOnMarket;
  const tempoMaximum = Math.max(...[currentDom, priorDom, cityAverageDom].filter((value): value is number => value !== undefined), 1);
  const tempoWidth = (value?: number) => (value === undefined ? '0%' : `${Math.max((value / tempoMaximum) * 100, 8)}%`);
  const domDifference = currentDom !== undefined && priorDom !== undefined ? Math.floor(currentDom - priorDom) : undefined;
  const cityDomDifference = currentDom !== undefined && cityAverageDom !== undefined ? currentDom - cityAverageDom : undefined;
  const marketPaceBuffer = cityAverageDom === undefined ? undefined : Math.max(3, cityAverageDom * 0.1);
  const isNearLocalPace = cityDomDifference === undefined || marketPaceBuffer === undefined || Math.abs(cityDomDifference) <= marketPaceBuffer;
  const marketPaceHeading = isNearLocalPace
    ? 'Homes are moving at the current local pace.'
    : `Homes are moving ${cityDomDifference < 0 ? 'faster' : 'slower'} than the current local pace.`;

  return (
    <>
      <article className="al-data-panel al-pricing-panel">
        <div className="al-panel-heading">
          <div>
            <p className="al-panel-kicker">Pricing movement</p>
            <h3>List prices and new-listing prices over time.</h3>
          </div>
          <div className="al-chart-legend" aria-label="Chart legend">
            <span>
              <i className="orange" />
              Median list
            </span>
            <span>
              <i className="green" />
              Median new listing
            </span>
          </div>
        </div>
        {series.medianListPrice.length ? <PricingChart list={series.medianListPrice} newListings={series.medianNewListingPrice || []} /> : <p>Historical pricing is not available for this ZIP code.</p>}
        <p className="al-chart-note">
          The orange line shows the median asking price across all active listings. The green dashed line shows the median asking price of newly listed homes, offering an earlier signal of how seller expectations are changing.
        </p>
      </article>

      <article className="al-data-panel al-tempo-panel">
        <div className="al-panel-heading">
          <div>
            <p className="al-panel-kicker">Market pace</p>
            <h3>{marketPaceHeading}</h3>
          </div>
          <DateRangeOutlinedIcon />
        </div>
        <div className="al-tempo-number">
          <strong>{formatMetric(currentDom, integer.format)}</strong>
          <span>
            average days
            <br />
            on market
          </span>
        </div>
        <div className="al-tempo-scale">
          <span style={{ '--w': tempoWidth(currentDom) } as React.CSSProperties}>
            <b>
              {zipCodeData.zipCode}
              {' '}
              now
            </b>
            <i>{formatMetric(currentDom, integer.format, ' days')}</i>
          </span>
          <span style={{ '--w': tempoWidth(priorDom) } as React.CSSProperties}>
            <b>90 days ago</b>
            <i>{formatMetric(priorDom, integer.format, ' days')}</i>
          </span>
          <span style={{ '--w': tempoWidth(cityAverageDom) } as React.CSSProperties}>
            <b>{`${titleCaseLocation(zipCodeData.locality.city) || 'City'} average`}</b>
            <i>{formatMetric(cityAverageDom, integer.format, ' days')}</i>
          </span>
        </div>
        <div className="al-tempo-callout">
          <ShowChartOutlinedIcon />
          <p>
            <strong className={domDifference === undefined || domDifference === 0 ? 'al-trend steady' : domDifference < 0 ? 'al-trend improving' : 'al-trend worsening'}>{domDifference === undefined ? 'Quarterly comparison unavailable' : `${Math.abs(domDifference)} days ${domDifference <= 0 ? 'faster' : 'slower'}`}</strong>
            <br />
            {domDifference === undefined ? 'More history is needed for this ZIP code.' : 'than this time last quarter'}
          </p>
        </div>
      </article>

      <article className="al-data-panel al-inventory-panel">
        <div className="al-panel-heading">
          <div>
            <p className="al-panel-kicker">Available homes</p>
            <h3>Inventory is changing across the local market.</h3>
          </div>
          <div className="al-panel-total">
            <strong>{formatMetric(metrics.inventory, integer.format)}</strong>
            <span>
              active
              <br />
              listings
            </span>
          </div>
        </div>
        {series.inventory.length ? <InventoryChart series={series.inventory} /> : <p>Historical inventory is not available.</p>}
        <p className="al-chart-note">{narrative?.inventory || 'Each bar is the final available weekly inventory observation for that month.'}</p>
      </article>

      <article className="al-data-panel al-outcomes-panel">
        <div className="al-panel-heading">
          <div>
            <p className="al-panel-kicker">Listing price movement</p>
            <h3>Current asking-price adjustments.</h3>
          </div>
        </div>
        <div className="al-outcome-list">
          <OutcomeBar label="Listings with price decreases" value={metrics.priceDecreasedPercent} color="decreased" />
          <OutcomeBar label="Listings with price increases" value={metrics.priceIncreasedPercent} color="increased" />
          <OutcomeBar label="Relisted homes" value={metrics.relistedPercent} color="relisted" />
        </div>
        <p className="al-chart-note">These are active-listing changes, not closed-sale outcomes.</p>
      </article>
    </>
  );
};

type Props = {
  agentLandingPageToken: string;
  agentData: IAgentLandingAgentData;
  zipCodeData: IAgentLandingMarketStats;
};

const DigitalAdZipCodeDataDisplay = ({ agentLandingPageToken, agentData, zipCodeData }: Props) => {
  const { metrics, series, narrative } = zipCodeData;
  const locality = [titleCaseLocation(zipCodeData.locality.city), fullStateName(zipCodeData.locality.state)].filter(Boolean).join(', ');
  const agents = normalizeAgents(agentData);
  const isTeam = agents.length > 1;
  const reportContext = `${locality || zipCodeData.zipCode}; Data${zipCodeData.updatedAt ? ` updated ${zipCodeData.updatedAt}` : ''}`;
  const inventoryMonthly = aggregateMonthlyLatest(series.inventory || []);
  const inventoryChange = inventoryMonthly.length > 1
    ? ((inventoryMonthly[inventoryMonthly.length - 1].value - inventoryMonthly[0].value) / Math.abs(inventoryMonthly[0].value || 1)) * 100
    : undefined;
  const pricingChange = percentChange(series.medianListPrice || []);
  const demandSummary = zipCodeData.marketPosition?.side === 'seller' ? 'Healthy' : zipCodeData.marketPosition?.side === 'buyer' ? 'Selective' : 'Balanced';
  const pricingSummary = pricingChange === undefined || Math.abs(pricingChange) < 1 ? 'Stabilizing' : pricingChange > 0 ? 'Rising' : 'Softening';
  const competitionSummary = inventoryChange === undefined || Math.abs(inventoryChange) < 5 ? 'Stable' : inventoryChange > 0 ? 'Slowly increasing' : 'Easing';
  const positionDescription = zipCodeData.marketPosition?.side === 'seller'
    ? 'Demand is keeping pace with available inventory.'
    : zipCodeData.marketPosition?.side === 'buyer'
      ? 'Available inventory is giving buyers more leverage.'
      : 'Supply and buyer activity are currently balanced.';

  return (
    <main className="al-market-results">
      <div className="al-shell al-results-shell">
        <header className="al-results-heading">
          <div>
            <Eyebrow>Real-time market profile</Eyebrow>
            <h2>
              Market data for
              {' '}
              {zipCodeData.zipCode}
            </h2>
            <p>
              {locality || zipCodeData.zipCode}
              {' '}
              · Updated
              {' '}
              {zipCodeData.updatedAt ? moment(zipCodeData.updatedAt).format('MMMM D, YYYY') : 'weekly'}
            </p>
          </div>
          {zipCodeData.marketPosition && (
            <aside className="al-market-position">
              <span>Current market position</span>
              <strong>{zipCodeData.marketPosition.label}</strong>
              <small>{positionDescription}</small>
            </aside>
          )}
        </header>

        <DigitalAdHeadlineMetrics zipCodeData={zipCodeData} />

        <div className="al-results-grid">
          <DigitalAdMarketCards zipCodeData={zipCodeData} />

          {series.medianRent.length > 0 && (
            <article className="al-data-panel al-rent-panel">
              <div className="al-panel-heading">
                <div>
                  <p className="al-panel-kicker">Rental market</p>
                  <h3>Median asking rent remains an important demand signal.</h3>
                </div>
                <div className="al-panel-total al-rent-total">
                  <strong>{formatMetric(metrics.medianRent, currency.format)}</strong>
                  <span>/ month</span>
                </div>
              </div>
              <RentChart series={series.medianRent} />
              <p className="al-chart-note">Monthly points use the final available weekly asking-rent observation.</p>
            </article>
          )}
        </div>

        <section className="al-market-summary">
          <div>
            <Eyebrow>What this means for you</Eyebrow>
            <h3>Price matters. Presentation matters more.</h3>
            <p>{narrative?.market || 'Use this weekly market snapshot as a starting point, then pair it with property condition, exact location, and verified comparable sales.'}</p>
          </div>
          <div className="al-summary-points">
            <span>
              <CheckOutlinedIcon />
              <b>Demand:</b>
              {' '}
              {demandSummary}
            </span>
            <span>
              <CheckOutlinedIcon />
              <b>Pricing:</b>
              {' '}
              {pricingSummary}
            </span>
            <span>
              <CheckOutlinedIcon />
              <b>Competition:</b>
              {' '}
              {competitionSummary}
            </span>
          </div>
        </section>

        <section className="al-results-form">
          <div className="al-results-form-copy">
            <Eyebrow light>Go from ZIP code to your front door</Eyebrow>
            <h3>Want your real-time home valuation?</h3>
            <p>
              {isTeam ? 'We’ll' : 'I’ll'}
              {' '}
              refine this neighborhood data with your home’s condition, updates, lot, and exact location—then send you a personalized value range. No automated estimate. No sales pressure.
            </p>
            {agents.map((agent) => <AgentAvatar key={`${agent.firstName}-${agent.lastName}`} agent={agent} compact />)}
          </div>
          <AgentLandingLeadForm
            agentLandingPageToken={agentLandingPageToken}
            agentData={agentData}
            leadResponseType={ELeadResponseType.AgentLandingMarketReportRequest}
            title="Request your home valuation"
            subtitle={`Tell ${isTeam ? 'us' : 'me'} which property this market report should be applied to.`}
            submitLabel="Request my home valuation"
            zipCode={zipCodeData.zipCode}
            reportContext={reportContext}
            showMessage={false}
            showSellingTimeline={false}
            formVariant="market"
            requestOptions={[{ value: 'Home valuation after market report', label: 'A property-specific home valuation' }, { value: 'Selling strategy after market report', label: 'A selling strategy conversation' }, { value: 'Buyer guidance after market report', label: 'Buyer guidance for this market' }]}
          />
        </section>
        <small className="al-market-source-note">Metrics describe active-listing conditions, not closed-sale prices.</small>
      </div>
    </main>
  );
};

export default DigitalAdZipCodeDataDisplay;
